import React, { useState, useEffect } from "react";
import {
  FormRowSelect,
  BirthdayMessageDetails,
  GeneralMessageDetails,
  ListSelection,
  FormRowSelectId,
} from "../components";
import Wrapper from "../assets/wrappers/DashboardFormPage";
import {
  useOutletContext,
  useLoaderData,
  redirect,
  useNavigation,
} from "react-router-dom";
import { toast } from "react-toastify";
import { customFetch } from "../utils/custom-fetch.js";

export const loader = async ({ request }) => {
  const params = Object.fromEntries(
    new URL(request.url).searchParams.entries()
  );

  try {
    params.limit = 99999;
    const [{ data: buildingsData }, { data: companies }] = await Promise.all([
      customFetch.get("/buildings", { params }),
      customFetch.get("/buildings/maintenance-companies"),
    ]);

    const maintenanceCompanies = [
      { _id: "הכל", name: "הכל" },
      ...companies.map((c) => ({
        _id: c.value,
        name: c.lastName ? `${c.name} ${c.lastName}` : c.name,
      })),
    ];

    return { data: buildingsData, maintenanceCompanies };
  } catch (err) {
    toast.error(err?.response?.data?.msg || "שגיאה בטעינת נתונים");
    throw err;
  }
};

const AllMessages = () => {
  const { user } = useOutletContext();
  const { data } = useLoaderData();

  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";

  const initialState = {
    author: user._id,
    messageType: "כללית",
    generalMessageDetails: {
      messageOption: "קבועה",
      startingDate: "",
      expirationDate: "",
      text: "",
    },
    birthdayDetails: {
      celebratorName: "",
      greeting: "",
      birthdayDate: "",
    },
  };

  const [formData, setFormData] = useState(initialState);
  const [targetType, setTargetType] = useState("global");
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedStreets, setSelectedStreets] = useState([]);
  const [selectedBuildings, setSelectedBuildings] = useState([]);

  const [availableCities, setAvailableCities] = useState([]);
  const [availableStreets, setAvailableStreets] = useState([]);
  const [availableBuildings, setAvailableBuildings] = useState([]);

  // Load available cities
  useEffect(() => {
    console.log(data);
    if (data?.buildings) {
      const cities = [...new Set(data.buildings.map((item) => item.city))];
      setAvailableCities(cities);
    }
  }, [data]);

  // Load available streets based on selected cities
  useEffect(() => {
    if (data?.buildings && selectedCities.length > 0) {
      const streets = [
        ...new Map(
          data.buildings
            .filter((item) => selectedCities.includes(item.city))
            .map((item) => [
              item.street,
              { city: item.city, street: item.street },
            ])
        ).values(),
      ];
      setAvailableStreets(streets);
    } else {
      setAvailableStreets([]);
    }
  }, [selectedCities, data]);

  // Load available buildings based on selected streets
  useEffect(() => {
    if (data?.buildings && selectedStreets.length > 0) {
      const buildings = data.buildings.filter((building) =>
        selectedStreets.includes(building.street)
      );
      setAvailableBuildings(buildings);
    } else {
      setAvailableBuildings([]);
    }
  }, [selectedStreets, data]);

  const handleTargetTypeChange = (e) => {
    const newTargetType = e.target.value;
    setTargetType(newTargetType);
    setSelectedCities([]);
    setSelectedStreets([]);
    setSelectedBuildings([]);
  };

  const handleCitySelection = (city) => {
    setSelectedCities((prev) =>
      prev.includes(city) ? prev.filter((c) => c !== city) : [...prev, city]
    );
  };

  const handleStreetSelection = (street) => {
    setSelectedStreets((prev) =>
      prev.includes(street)
        ? prev.filter((s) => s !== street)
        : [...prev, street]
    );
  };

  const handleBuildingSelection = (building) => {
    setSelectedBuildings((prev) =>
      prev.includes(building)
        ? prev.filter((b) => b !== building)
        : [...prev, building]
    );
  };

  // Handle input changes for general message details
  const handleGeneralInputChange = (e) => {
    const { name, value } = e.target;
    const fieldName = name.replace('generalMessageDetails.', '');
    
    setFormData(prev => ({
      ...prev,
      generalMessageDetails: {
        ...prev.generalMessageDetails,
        [fieldName]: value
      }
    }));
  };

  // Handle input changes for birthday details
  const handleBirthdayInputChange = (e) => {
    const { name, value } = e.target;
    const fieldName = name.replace('birthdayDetails.', '');
    
    setFormData(prev => ({
      ...prev,
      birthdayDetails: {
        ...prev.birthdayDetails,
        [fieldName]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const messageData = {
        messageType: formData.messageType,
        messageDetails: formData.messageType === "יום הולדת" 
          ? formData.birthdayDetails 
          : formData.generalMessageDetails,
        target: {
          type: targetType,
          cities: selectedCities,
          streets: selectedStreets,
          buildings: selectedBuildings,
        },
      };
      
      await customFetch.post("/messages", messageData);
      toast.success("ההודעה נוצרה בהצלחה!");

      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.log(error.response?.data.error);
      
      toast.error(error?.response?.data?.msg || error?.response?.data?.error || "שגיאה ביצירת הודעה");
      console.error("Error creating message:", error);
    }
  };

  const handleSelectAllStreets = () => {
    if (selectedStreets.length === availableStreets.length) {
      setSelectedStreets([]);
    } else {
      setSelectedStreets(availableStreets.map((s) => s.street));
    }
  };

  const handleSelectAllCities = () => {
    if (selectedCities.length === availableCities.length) {
      setSelectedCities([]);
    } else {
      setSelectedCities(availableCities);
    }
  };

  if (data.buildings.length === 0) {
    return (
      <Wrapper className="full-page">
        <h4>אין בניינים במערכת</h4>
      </Wrapper>
    );
  }

  return (
    <Wrapper className="full-page">
      {isSubmitting && <div>טוען...</div>}
      <form method="post" className="form" onSubmit={handleSubmit}>
        <h4 className="form-title">הוספת הודעה</h4>

        <div className="form-center">
          <FormRowSelectId
            labelText="היקף ההודעה"
            name="targetType"
            value={targetType}
            handleChange={handleTargetTypeChange}
            idField="id"
            displayField="name"
            list={[
              { id: "global", name: "כל הבניינים" },
              { id: "city", name: "ערים נבחרות" },
              { id: "street", name: "רחובות נבחרים" },
              { id: "building", name: "בניינים ספציפיים" },
            ]}
          />
          
          {targetType === "city" && (
            <ListSelection
              labelText="בחר ערים"
              items={availableCities}
              selectedItems={selectedCities}
              handleItemSelection={handleCitySelection}
            />
          )}
          
          {targetType === "street" && (
            <>
              <ListSelection
                labelText="בחר ערים"
                items={availableCities}
                selectedItems={selectedCities}
                handleItemSelection={handleCitySelection}
                handleSelectAll={handleSelectAllCities}
              />
              <ListSelection
                labelText="בחר רחובות"
                items={availableStreets.map((s) => s.street)}
                selectedItems={selectedStreets}
                handleItemSelection={handleStreetSelection}
                handleSelectAll={handleSelectAllStreets}
              />
            </>
          )}

          {targetType === "building" && (
            <>
              <ListSelection
                labelText="בחר ערים"
                items={availableCities}
                selectedItems={selectedCities}
                handleItemSelection={handleCitySelection}
                handleSelectAll={handleSelectAllCities}
              />
              <ListSelection
                labelText="בחר רחובות"
                items={availableStreets.map((s) => s.street)}
                selectedItems={selectedStreets}
                handleItemSelection={handleStreetSelection}
                handleSelectAll={handleSelectAllStreets}
              />
              <ListSelection
                labelText="בחר בניינים"
                items={availableBuildings.map((b) => ({
                  id: b._id,
                  label: `${b.street} ${b.houseNumber}`,
                }))}
                selectedItems={selectedBuildings}
                handleItemSelection={handleBuildingSelection}
              />
            </>
          )}
        </div>

        <div className="form-center">
          <FormRowSelect
            labelText="סוג ההודעה"
            name="messageType"
            value={formData.messageType}
            handleChange={(e) =>
              setFormData({ ...formData, messageType: e.target.value })
            }
            list={["כללית", "יום הולדת"]}
          />

          {formData.messageType === "יום הולדת" && (
            <BirthdayMessageDetails
              formData={formData.birthdayDetails}
              handleInputChange={handleBirthdayInputChange}
            />
          )}

          {formData.messageType === "כללית" && (
            <GeneralMessageDetails
              formData={formData.generalMessageDetails}
              handleInputChange={handleGeneralInputChange}
            />
          )}

          <div className="btn-container">
            <button
              type="submit"
              className="btn btn-block form-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? "שומר..." : "שמירה"}
            </button>
            <button
              type="button"
              className="btn btn-block clear-btn"
              onClick={() => {
                setFormData(initialState);
                setTargetType("global");
                setSelectedCities([]);
                setSelectedStreets([]);
                setSelectedBuildings([]);
              }}
            >
              מחיקה
            </button>
          </div>
        </div>
      </form>
    </Wrapper>
  );
};

export default AllMessages;